import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { User, ChevronDown, ChevronUp, Loader2, Check, Star, Pencil } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { createLocalDate } from "@/utils/dateUtils";
import { supabase } from "@/integrations/supabase/client";
import { writeExpectingRows } from "@/lib/supabase-write";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  CRITERIOS_AVALIACAO_AGENCIA,
  type CriterioSlug,
  type AvaliacaoAgenciaListItem,
} from "@/types/avaliacao";

interface AvaliacaoAgenciaCardProps {
  avaliacao: AvaliacaoAgenciaListItem;
}

function getDefaultNotas(): Record<CriterioSlug, number> {
  const defaults = {} as Record<CriterioSlug, number>;
  for (const c of CRITERIOS_AVALIACAO_AGENCIA) {
    defaults[c.slug] = 5;
  }
  return defaults;
}

function getProjectStatus(dataConclusao?: string | null): { label: string; variant: "default" | "secondary" } {
  if (!dataConclusao) return { label: "Ativo", variant: "default" };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const conclusao = createLocalDate(dataConclusao);
  return conclusao > hoje
    ? { label: "Ativo", variant: "default" }
    : { label: "Finalizado", variant: "secondary" };
}

function formatRenovacao(n: number): string {
  if (n === 1) return "1ª renovação";
  if (n === 2) return "2ª renovação";
  if (n === 3) return "3ª renovação";
  return `${n}ª renovação`;
}

export function AvaliacaoAgenciaCard({ avaliacao }: AvaliacaoAgenciaCardProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const jaAvaliado = !!avaliacao.minha_avaliacao;

  // Pendentes começam abertos, avaliados começam fechados
  const [expanded, setExpanded] = useState(!jaAvaliado);
  const [editingNucleo, setEditingNucleo] = useState(false);
  const [nucleoValue, setNucleoValue] = useState(avaliacao.project_nucleo ?? "");
  const nucleoInputRef = useRef<HTMLInputElement>(null);
  const [notas, setNotas] = useState<Record<CriterioSlug, number>>(() =>
    avaliacao.minha_avaliacao ? { ...getDefaultNotas(), ...avaliacao.minha_avaliacao.notas } : getDefaultNotas()
  );
  const [comentario, setComentario] = useState(avaliacao.minha_avaliacao?.comentario ?? "");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const projectStatus = getProjectStatus(avaliacao.project_end_date);

  const saveNucleo = useCallback(async () => {
    const trimmed = nucleoValue.trim();
    const valorFinal = trimmed || null;
    if (valorFinal === (avaliacao.project_nucleo ?? null)) {
      setEditingNucleo(false);
      return;
    }
    try {
      await writeExpectingRows(
        supabase
          .from("controle_projetos")
          .update({ nucleo: valorFinal })
          .eq("id", avaliacao.controle_projeto_id),
        "salvar núcleo",
      );
      queryClient.invalidateQueries({ queryKey: ["avaliacoes-agencia"] });
      setEditingNucleo(false);
    } catch (error: unknown) {
      console.error("Erro ao salvar núcleo:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o núcleo. Nada foi alterado.",
        variant: "destructive",
      });
      // Mantém o modo de edição aberto para o usuário reaver o valor digitado.
    }
  }, [nucleoValue, avaliacao.project_nucleo, avaliacao.controle_projeto_id, queryClient, toast]);

  // Média das notas do usuário
  const mediaNotas = useMemo(() => {
    if (!avaliacao.minha_avaliacao) return null;
    const notasObj = avaliacao.minha_avaliacao.notas;
    const slugs = CRITERIOS_AVALIACAO_AGENCIA.map((c) => c.slug);
    const soma = slugs.reduce((acc, slug) => acc + (notasObj[slug] ?? 5), 0);
    return soma / slugs.length;
  }, [avaliacao.minha_avaliacao]);

  // Edição ainda não gravada. Enquanto existir, o eco do servidor não
  // sobrescreve a tela, e sair da tela grava na hora em vez de descartar.
  const pendingSave = useRef<{ notas: Record<CriterioSlug, number>; comentario: string } | null>(null);

  // Sync notas e comentário when external data changes
  useEffect(() => {
    if (pendingSave.current) return;
    if (avaliacao.minha_avaliacao) {
      setNotas({ ...getDefaultNotas(), ...avaliacao.minha_avaliacao.notas });
      setComentario(avaliacao.minha_avaliacao.comentario ?? "");
    }
  }, [avaliacao.minha_avaliacao]);

  const saveAvaliacao = useCallback(async (notasToSave: Record<CriterioSlug, number>, comentarioToSave: string) => {
    if (!user) return;
    setSaveStatus("saving");
    if (savedTimer.current) clearTimeout(savedTimer.current);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("avaliacoes_usuario_agencia")
        .upsert(
          {
            job_evaluation_id: avaliacao.id,
            user_id: user.id,
            notas: notasToSave,
            comentario: comentarioToSave,
          },
          { onConflict: "job_evaluation_id,user_id" }
        );

      if (error) throw error;

      if (pendingSave.current?.notas === notasToSave && pendingSave.current?.comentario === comentarioToSave) {
        pendingSave.current = null;
      }
      setSaveStatus("saved");
      savedTimer.current = setTimeout(() => setSaveStatus("idle"), 3000);

      queryClient.invalidateQueries({ queryKey: ["avaliacoes-agencia"] });
      queryClient.invalidateQueries({ queryKey: ["minhas-avaliacoes-agencia"] });
      queryClient.invalidateQueries({ queryKey: ["todas-avaliacoes-agencia"] });
    } catch (error: unknown) {
      console.error("Erro ao salvar avaliação:", error);
      setSaveStatus("idle");
      toast({
        title: "Não foi possível salvar a avaliação",
        description: "Suas notas continuam na tela. Mexa em qualquer nota para tentar de novo.",
        variant: "destructive",
      });
    }
  }, [avaliacao.id, user, queryClient, toast]);

  const saveAvaliacaoRef = useRef(saveAvaliacao);
  saveAvaliacaoRef.current = saveAvaliacao;

  const scheduleSave = useCallback((updatedNotas: Record<CriterioSlug, number>, updatedComentario: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSaveStatus("idle");
    pendingSave.current = { notas: updatedNotas, comentario: updatedComentario };
    debounceTimer.current = setTimeout(() => {
      saveAvaliacao(updatedNotas, updatedComentario);
    }, 1500);
  }, [saveAvaliacao]);

  const handleNotaChange = (slug: CriterioSlug, value: number) => {
    const updated = { ...notas, [slug]: value };
    setNotas(updated);
    scheduleSave(updated, comentario);
  };

  const handleComentarioChange = (value: string) => {
    setComentario(value);
    scheduleSave(notas, value);
  };

  // Sair da tela com edição pendente grava na hora (antes descartava).
  useEffect(() => {
    const warnBeforeClose = (event: BeforeUnloadEvent) => {
      if (!pendingSave.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeClose);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeClose);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      const pending = pendingSave.current;
      if (pending) void saveAvaliacaoRef.current(pending.notas, pending.comentario);
    };
  }, []);

  // Datas de vigência formatadas
  const dataVigencia = useMemo(() => {
    if (!avaliacao.project_start_date) return null;
    const inicio = format(createLocalDate(avaliacao.project_start_date), "dd/MM/yy", { locale: ptBR });
    const fim = avaliacao.project_end_date
      ? format(createLocalDate(avaliacao.project_end_date), "dd/MM/yy", { locale: ptBR })
      : "em aberto";
    return `${inicio} até ${fim}`;
  }, [avaliacao.project_start_date, avaliacao.project_end_date]);

  return (
    <Card
      className={`transition-all duration-200 border-l-4 ${
        jaAvaliado
          ? "border-l-green-500 bg-green-50/50 dark:bg-green-950/20"
          : projectStatus.label === "Ativo"
            ? "border-l-orange-400 bg-orange-50/50 dark:bg-orange-950/20"
            : "border-l-orange-300/50 bg-stone-50/50 dark:bg-stone-950/20"
      }`}
    >
      <CardContent className="p-0">
        {/* Header - always visible, clickable */}
        <div
          className="flex items-center justify-between gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          {/* Freelancer + nota + avaliadores */}
          <div className="flex items-center gap-2 min-w-0 w-64">
            <User className="h-4 w-4 text-muted-foreground shrink-0 self-start mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-sm truncate">
                  {avaliacao.freelancer_name || "Não identificado"}
                </p>
                {mediaNotas !== null && (
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
                    <span className="text-sm font-semibold text-yellow-600">{mediaNotas.toFixed(1)}</span>
                  </div>
                )}
              </div>
              {avaliacao.avaliadores && avaliacao.avaliadores.length > 0 && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <span>Avaliado por:</span>
                  <span className="inline-flex items-center h-4 px-1.5 rounded-full bg-muted text-[10px] font-semibold text-muted-foreground truncate max-w-[120px]">
                    {avaliacao.avaliadores[0].full_name}
                  </span>
                  {avaliacao.avaliadores.length > 1 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center h-4 px-1.5 rounded-full bg-muted text-[10px] font-semibold text-muted-foreground cursor-default shrink-0">
                            +{avaliacao.avaliadores.length - 1}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start">
                          <div className="text-xs space-y-0.5">
                            {avaliacao.avaliadores.slice(1).map((av) => (
                              <div key={av.user_id}>{av.full_name}</div>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Vigência do período + renovação */}
          {dataVigencia && (
            <div className="flex flex-col items-start gap-0.5 text-xs text-muted-foreground shrink-0 hidden sm:flex">
              <span><span className="font-medium">Vigência:</span> {dataVigencia}</span>
              {(avaliacao.numero_renovacoes ?? 0) > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-blue-600 border-blue-300">
                  {formatRenovacao(avaliacao.numero_renovacoes!)}
                </Badge>
              )}
            </div>
          )}

          {/* Status do projeto */}
          <Badge
            variant={projectStatus.variant}
            className={`shrink-0 ${
              projectStatus.label === "Ativo"
                ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
            }`}
          >
            {projectStatus.label}
          </Badge>

          {/* Núcleo */}
          <div
            className="shrink-0 w-36 flex items-center justify-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {editingNucleo ? (
              <>
                <Input
                  ref={nucleoInputRef}
                  value={nucleoValue}
                  onChange={(e) => setNucleoValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveNucleo();
                    if (e.key === "Escape") {
                      setNucleoValue(avaliacao.project_nucleo ?? "");
                      setEditingNucleo(false);
                    }
                  }}
                  onBlur={saveNucleo}
                  className="h-6 text-xs w-24 px-1.5"
                  autoFocus
                />
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    saveNucleo();
                  }}
                  className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                {avaliacao.project_nucleo ? (
                  <Badge variant="outline" className="text-xs">
                    {avaliacao.project_nucleo}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground/50 italic">
                    Nenhum núcleo atribuído
                  </span>
                )}
                <button
                  onClick={() => {
                    setNucleoValue(avaliacao.project_nucleo ?? "");
                    setEditingNucleo(true);
                  }}
                  className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </>
            )}
          </div>

          {/* Status da avaliação */}
          {jaAvaliado ? (
            <Badge className="bg-green-600 hover:bg-green-700 text-white shrink-0">
              Avaliado
            </Badge>
          ) : (
            <Badge variant="outline" className="shrink-0 text-orange-600 border-orange-300">
              Pendente
            </Badge>
          )}

          {/* Expand/collapse */}
          <div className="shrink-0">
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Expanded body - inline form */}
        {expanded && (
          <div className="border-t px-4 pb-4 pt-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-muted-foreground">
                Avalie de 1 a 10 cada critério. As notas são salvas automaticamente.
              </p>
              {saveStatus === "saving" && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Salvando...</span>
                </div>
              )}
              {saveStatus === "saved" && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <Check className="h-3 w-3" />
                  <span>Salvo</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {CRITERIOS_AVALIACAO_AGENCIA.map((criterio) => (
                <div key={criterio.slug} className="space-y-1">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs text-muted-foreground">
                      {criterio.label}
                    </Label>
                    <Badge variant="secondary" className="font-mono text-xs h-5 min-w-[28px] justify-center">
                      {notas[criterio.slug]}
                    </Badge>
                  </div>
                  <Slider
                    min={1}
                    max={10}
                    step={1}
                    value={[notas[criterio.slug]]}
                    onValueChange={(vals) => handleNotaChange(criterio.slug, vals[0])}
                    className="py-1"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-1">
              <Label className="text-xs text-muted-foreground">Comentário (opcional)</Label>
              <Textarea
                placeholder="Escreva observações sobre esse período com o profissional..."
                value={comentario}
                onChange={(e) => handleComentarioChange(e.target.value)}
                className="min-h-[60px] text-sm resize-y"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
